import { useEffect, useState } from "react";
import heroHome from "@/assets/hero-home.webp";
import heroHomeMobile from "@/assets/hero-home-mobile.webp";

/**
 * HeroWindowBackground
 * --------------------
 * Brand flowing-gradient (teal → aqua → yellow) with a softly visible
 * home photo behind it. Sun-screen mesh rolls down on scroll — kept
 * subtle so the brand colors and image stay readable.
 */

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

export default function HeroWindowAnimation() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let ticking = false;
    const update = () => {
      ticking = false;
      const vh = window.innerHeight || 1;
      const p = clamp01(window.scrollY / (vh * 0.18));
      const r = Math.round(p * 1000) / 1000;
      setProgress((prev) => (prev === r ? prev : r));
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", update);
    };
  }, []);

  const p = progress;
  const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;

  const meshY = lerp(-100, 0, eased);
  const meshOpacity = lerp(0, 0.5, Math.min(1, eased * 1.3));

  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden>
      {/* Background home photo — mobile-first source for fast LCP, larger
          asset only on tablet+. Explicit width/height prevents CLS. */}
      <picture>
        <source media="(max-width: 767px)" srcSet={heroHomeMobile} />
        <img
          src={heroHome}
          alt=""
          width={1600}
          height={900}
          className="absolute inset-0 w-full h-full object-cover opacity-60"
          loading="eager"
          // @ts-expect-error fetchpriority is a valid HTML attr but not yet typed in React
          fetchpriority="high"
          decoding="async"
        />
      </picture>
      {/* Signature flowing-gradient wash — same diagonal teal→aqua→green→yellow
          used by CTABanner so the logo and hero feel like one continuous brand. */}
      <div className="absolute inset-0 bg-flowing-gradient opacity-80" />

      {/* SUN SCREEN MESH — rolls down across the whole hero on scroll */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute left-0 right-0 top-0 h-full will-change-transform"
          style={{ transform: `translate3d(0, ${meshY}%, 0)` }}
        >
          <div
            className="w-full h-full"
            style={{
              background:
                "repeating-linear-gradient(0deg, hsl(174 72% 14% / 0.7) 0px, hsl(174 72% 14% / 0.7) 1px, transparent 1px, transparent 5px), repeating-linear-gradient(90deg, hsl(174 72% 14% / 0.7) 0px, hsl(174 72% 14% / 0.7) 1px, transparent 1px, transparent 5px)",
              opacity: meshOpacity,
            }}
          />
          {/* Bottom rail — bright aqua → teal with yellow glow underneath */}
          <div
            className="absolute left-0 right-0 bottom-0 h-[14px] md:h-[18px]"
            style={{
              background:
                "linear-gradient(180deg, hsl(var(--aqua)) 0%, hsl(var(--primary)) 100%)",
              boxShadow:
                "0 -2px 0 hsl(var(--sun-yellow) / 0.55), 0 6px 20px hsl(174 72% 8% / 0.45), inset 0 1px 0 hsl(0 0% 100% / 0.25)",
            }}
          />
        </div>
      </div>
    </div>
  );
}
